import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Mail, Phone, Building2, Briefcase, FileText, Users, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../lib/site";
import { fadeUp } from "../components/sections";
import { getAdminToken, setAdminToken } from "./AdminLoginPage";

const TABS = [
  { id: "leads", label: "Lead Magnet", icon: FileText, endpoint: "/admin/leads" },
  { id: "contacts", label: "Contact Form", icon: Mail, endpoint: "/admin/contacts" },
  { id: "applications", label: "Applications", icon: Briefcase, endpoint: "/admin/applications" },
];

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return s;
  }
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [active, setActive] = useState("leads");
  const [data, setData] = useState({ leads: null, contacts: null, applications: null });
  const [loading, setLoading] = useState(false);

  const token = getAdminToken();
  const authHeader = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) {
      navigate("/admin", { replace: true });
      return;
    }
    apiClient
      .get("/admin/me", { headers: authHeader })
      .then((r) => setMe(r.data))
      .catch(() => {
        setAdminToken(null);
        navigate("/admin", { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!me) return;
    const tab = TABS.find((t) => t.id === active);
    if (!tab) return;
    if (data[active] !== null) return;
    setLoading(true);
    apiClient
      .get(tab.endpoint, { headers: authHeader })
      .then((r) => setData((d) => ({ ...d, [active]: r.data })))
      .catch(() => toast.error("Couldn't load that list."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, me]);

  const logout = () => {
    setAdminToken(null);
    toast.success("Signed out.");
    navigate("/admin", { replace: true });
  };

  if (!me) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center">
        <p className="text-zinc-500 text-sm">Authenticating…</p>
      </div>
    );
  }

  const items = data[active];

  return (
    <div data-testid="admin-dashboard-page" className="min-h-[calc(100vh-80px)] px-6 md:px-12 lg:px-16 py-12 md:py-16">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 pb-10 border-b border-white/10">
        <div>
          <p className="overline mb-3">Admin Dashboard</p>
          <h1 className="font-serif-display text-4xl md:text-5xl text-white font-light leading-tight">
            Hello, {me.name}<span style={{ color: "#C2533B" }}>.</span>
          </h1>
          <p className="text-zinc-500 text-sm mt-3">{me.email}</p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <Link to="/admin/analytics" className="btn-secondary" data-testid="admin-analytics-link">
            Analytics <BarChart3 size={14} strokeWidth={1.8} />
          </Link>
          <Link to="/" className="btn-secondary" data-testid="admin-back-to-site">View Site</Link>
          <button onClick={logout} className="btn-secondary" data-testid="admin-logout">
            Sign Out <LogOut size={14} strokeWidth={1.8} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-12 flex flex-wrap gap-px bg-[#27272A] border border-[#27272A]" role="tablist">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = active === tab.id;
          const count = data[tab.id]?.length;
          return (
            <button
              key={tab.id}
              onClick={() => setActive(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
              className={`flex-1 min-w-[180px] px-6 py-5 transition-colors flex items-center justify-between gap-4 ${
                isActive ? "bg-[#C2533B] text-white" : "bg-[#0A0A0C] text-zinc-400 hover:text-white"
              }`}
            >
              <span className="flex items-center gap-3">
                <Icon size={16} strokeWidth={1.5} />
                <span className="text-sm font-medium tracking-wide uppercase">{tab.label}</span>
              </span>
              {typeof count === "number" && (
                <span className={`text-xs ${isActive ? "text-white/80" : "text-zinc-600"}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <motion.div {...fadeUp} className="mt-12">
        {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
        {!loading && items && items.length === 0 && (
          <div className="border border-[#27272A] bg-[#0A0A0C] p-12 text-center">
            <Users size={24} strokeWidth={1.2} style={{ color: "#C2533B" }} className="mx-auto mb-4" />
            <p className="text-zinc-400 text-sm">Nothing here yet. New entries appear in real time.</p>
          </div>
        )}
        {!loading && items && items.length > 0 && (
          <div className="space-y-px bg-[#27272A] border border-[#27272A]" data-testid={`admin-list-${active}`}>
            {items.map((item, idx) => (
              <ListRow key={item.id || idx} type={active} item={item} />
            ))}
          </div>
        )}
      </motion.div>

      <p className="text-zinc-600 text-xs mt-12 leading-relaxed">
        New submissions also sync to HubSpot CRM automatically and trigger email notifications to
        info@elitesolutionshub.com.
      </p>
    </div>
  );
}

function ListRow({ type, item }) {
  return (
    <article className="bg-[#0A0A0C] p-6 md:p-8" data-testid={`admin-row-${item.id}`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
        <div className="md:col-span-3">
          <p className="font-serif-display text-xl text-white font-light leading-tight">
            {item.name}
          </p>
          <p className="text-zinc-500 text-xs mt-1">{fmtDate(item.created_at)}</p>
        </div>

        <div className="md:col-span-3 space-y-1 text-sm">
          <p className="flex items-center gap-2 text-zinc-300">
            <Mail size={13} strokeWidth={1.5} className="text-zinc-500" />
            <a href={`mailto:${item.email}`} className="hover:text-white transition truncate">
              {item.email}
            </a>
          </p>
          {item.phone && (
            <p className="flex items-center gap-2 text-zinc-400 text-xs">
              <Phone size={12} strokeWidth={1.5} className="text-zinc-600" />
              {item.phone}
            </p>
          )}
          {item.company && (
            <p className="flex items-center gap-2 text-zinc-400 text-xs">
              <Building2 size={12} strokeWidth={1.5} className="text-zinc-600" />
              {item.company}
            </p>
          )}
          {item.role && (
            <p className="flex items-center gap-2 text-zinc-400 text-xs">
              <Briefcase size={12} strokeWidth={1.5} className="text-zinc-600" />
              {item.role}
            </p>
          )}
        </div>

        <div className="md:col-span-5 text-zinc-300 text-sm leading-relaxed">
          {type === "contacts" && (
            <>
              {item.service && <p className="text-xs text-zinc-500 mb-2">Service: {item.service} · Budget: {item.budget || "—"}</p>}
              <p className="line-clamp-4">{item.message}</p>
            </>
          )}
          {type === "applications" && (
            <>
              <p className="text-xs text-zinc-500 mb-2">
                Applied for <span className="text-white">{item.role_title}</span>
                {item.portfolio_url && (
                  <> · <a href={item.portfolio_url} target="_blank" rel="noreferrer" className="text-[#C2533B] hover:underline">portfolio</a></>
                )}
              </p>
              <p className="line-clamp-4">{item.cover_letter}</p>
            </>
          )}
          {type === "leads" && (
            <p className="text-xs text-zinc-500">
              Downloaded: <span className="text-white">Diligence Checklist</span>
            </p>
          )}
        </div>

        <div className="md:col-span-1 flex md:justify-end items-start">
          <span
            className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${
              item.email_sent
                ? "border-[#C2533B] text-[#C2533B]"
                : "border-zinc-700 text-zinc-500"
            }`}
            title={item.email_sent ? "Email delivered" : "Email not sent"}
          >
            {item.email_sent ? "Emailed" : "Pending"}
          </span>
        </div>
      </div>
    </article>
  );
}
